import { useAppSelector } from "app/hooks";
import { useAuth } from "hooks/auth";
import { querystring } from "models/Listing";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import useSWR, { SWRResponse } from "swr";
import { ExportListItem, ExportTypeUnion } from "types/exports";
import { ListResponse, ListingProps } from "types/listing";
import { getEnvVariable } from "utilities/env";

export function useExports() {
  const { code } = useAppSelector(selectCurrentStoreData);
  const { fetcher } = useAuth();

  const exportList = async (type: ExportTypeUnion, props?: any) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/export/${type}`,
      {
        body: JSON.stringify(props),
        headers: { "Content-Type": "application/json" },
        method: "POST",
      },
    );

    const data = await response?.json();
    if (response?.ok) {
      return true;
    } else {
      throw data;
    }
  };

  const useExportsList = (
    props?: ListingProps,
  ): SWRResponse<ListResponse<ExportListItem>, any> => {
    return useSWR(
      () =>
        code
          ? `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/export${querystring(props)}`
          : null,
      fetcher,
    );
  };

  const downloadExport = async (exportId: string) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${code}/export/${exportId}/download`,
    );

    if (response.ok) {
      const contentDisposition = response.headers.get("Content-Disposition");
      const filename = contentDisposition
        ? contentDisposition.split("filename=")[1]?.replace(/"/g, "")
        : "downloaded-file";
      // Create a writable stream to process chunks
      const fileStream = response.body;
      const reader = fileStream.getReader();
      const chunks = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        chunks.push(value);
      }

      // Combine all chunks into a single Blob
      const blob = new Blob(chunks);
      // Create a URL for the Blob and trigger the download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();

      // Revoke the URL to free up resources
      window.URL.revokeObjectURL(url);
    }
  };

  return {
    exportList,
    useExportsList,
    downloadExport,
  };
}
